import { prepareSkyCinderPose, skyCinderPose } from './128-sky-cinderheart-pose.mjs';
export { installSkyCinderheartSlagRig } from './119-sky-cinderheart-slag.mjs';
import {
  markSkyCinderheartMolten,
  resolveSkyCinderheartSlagContact,
} from './119-sky-cinderheart-slag.mjs';
import {
  compileSkyAuthoredRig,
  sampleSkyAuthoredPose,
  skyAuthoredPoseContact,
} from './115-sky-authored-pose.mjs';
import { skyTerrainHeight } from './87-sky-terrain.mjs';

const CLIPS = ['Idle', 'TankRupture', 'ValveReset', 'PressureRelease'];
const JOINTS = ['coolant_joint_00', 'coolant_joint_01', 'coolant_joint_02', 'coolant_joint_03'];
const DURATION = {
  approach: 4,
  crawl: 6,
  charge: 2,
  eruption: 1,
  locked: 1,
  rolling: 4,
  exposed: 6,
  righting: 5,
};
const CLIP = {
  approach: 'Idle',
  crawl: 'Crawl',
  charge: 'VentCharge',
  eruption: 'Erupt',
  locked: 'CoolantLock',
  rolling: 'RollOver',
  exposed: 'UndersideHold',
  righting: 'RightSelf',
};

/** Install the exact source-backed coolant assembly from asset readiness.
 * Required release/valve sockets and clips are validated; no gameplay progress
 * or interaction exists until the actual boss encounter creates its reservoirs. */
export function installSkyCinderheartPropRig(state, source) {
  const rig = compileSkyAuthoredRig(source);
  if (
    !state ||
    rig?.worldId !== 'cinderheart-coolant' ||
    !CLIPS.every((name) => rig.clips.some((clip) => clip.name === name)) ||
    !['valve_mount_4', 'valve_mount_5', 'coolant_release_0', 'coolant_release_2'].every((name) =>
      rig.sockets.some((socket) => socket.name === name),
    )
  )
    return false;
  state.authoredPropRigs ??= {};
  state.authoredPropRigs['cinderheart-coolant'] = rig;
  if (state.authoredBossRigs?.cinderheart) prepareSkyCinderPose(state.authoredBossRigs.cinderheart);
  return true;
}

/** The only extra combat targets are these finite physical reservoirs. Broken
 * or resetting tanks remain visible but cannot absorb or score another hit. */
export function skyCinderheartTargets(state) {
  return state.boss?.cinderheart ? (state.boss.authoredProps ?? []) : [];
}
function propPose(prop, clip, seconds) {
  prop.authoredPose = sampleSkyAuthoredPose(prop.authoredRig, clip, seconds, prop.rootTransform);
  const socket = prop.authoredPose.sockets[prop.valve];
  prop.weakpoint = {
    ...socket,
    id: prop.valve,
    radius: 2.5,
    criticalRadius: 1.3,
    exposed: prop.collisionEnabled,
  };
  prop.weakpoints = [prop.weakpoint];
  const bounds = prop.authoredPose.bounds;
  prop.body = {
    x: prop.x,
    y: prop.y,
    z: bounds.max[2],
    radius: Math.max(...bounds.max.map((v, i) => v - bounds.min[i])) / 2,
  };
}

/** Two original28m reservoirs sit outside the12m flight corridor. All hits use
 * their source triangles. No box collider, decorative clone or hidden tank can
 * trigger a lock. The run owns both prop IDs and all active simulation clocks. */
export function createSkyCinderheart(state, boss) {
  const rig = state.authoredPropRigs?.['cinderheart-coolant'];
  if (boss.worldId !== 'cinderheart' || !rig) return false;
  if (
    !['CoolantLock', 'RollOver', 'UndersideHold', 'RightSelf'].every((name) =>
      boss.authoredRig.clips.some((clip) => clip.name === name),
    )
  )
    return false;
  prepareSkyCinderPose(boss.authoredRig);
  boss.cinderheart = {
    phase: 'approach',
    age: 0,
    frozen: [false, false, false, false],
    frozenPose: [],
    lockedJoints: [],
    frozenAt: [0, 0, 0, 0],
    broken: [false, false, false, false],
    fracturedAt: [0, 0, 0, 0],
    streams: [],
    rolls: 0,
    volley: 0,
  };
  boss.authoredProps = [-1, 1].map((side) => {
    const x = side * 34,
      z = -46,
      y = skyTerrainHeight(x, z, state.terrainTravel ?? 0) - rig.bounds.min[1];
    const prop = {
      id: state.nextId++,
      kind: 'authored-prop',
      artVariant: 'cinderheart-coolant',
      worldId: 'cinderheart-coolant',
      authoredRig: rig,
      x,
      y,
      z,
      hp: 6,
      maxHp: 6,
      side,
      age: 0,
      phase: 'ready',
      collisionEnabled: false,
      cinderheartTank: true,
      valve: side < 0 ? 'valve_mount_5' : 'valve_mount_4',
      release: side < 0 ? 'coolant_release_2' : 'coolant_release_0',
      rootTransform: { position: [x, y, z], quaternion: [0, 0, 0, 1], scale: [1, 1, 1] },
    };
    propPose(prop, 'Idle', 0);
    return prop;
  });
  return true;
}
function freezeJoint(boss, index) {
  const systems = boss.cinderheart;
  if (systems.frozen[index]) return;
  systems.frozen[index] = true;
  systems.frozenAt[index] = boss.age;
  // Freeze the three actual articulated joints in their current local pose.
  // The body can still turn/roll around them; rendering and contact share this
  // numeric override, so a stopped limb never keeps moving invisibly in physics.
  boss.authoredRig.nodes.forEach((node, i) => {
    if (
      new RegExp(`^leg_0${index}_[0-2]$`).test(node.name) &&
      !systems.lockedJoints.some((joint) => joint.node === i)
    )
      systems.lockedJoints.push({ node: i, ...structuredClone(boss.authoredPose.nodes[i]) });
  });
}
function releaseCoolant(state, prop) {
  const boss = state.boss,
    systems = boss.cinderheart;
  prop.phase = 'ruptured';
  prop.age = 0;
  prop.collisionEnabled = false;
  propPose(prop, 'TankRupture', 0);
  const from = prop.authoredPose.sockets[prop.release],
    indices = prop.side < 0 ? [2, 3] : [0, 1];
  for (const index of indices) {
    const target = boss.authoredPose.sockets[JOINTS[index]],
      length = Math.hypot(target.x - from.x, target.y - from.y, target.z - from.z);
    systems.streams.push({
      id: state.nextId++,
      from: { ...from },
      head: { ...from },
      // Fluid keeps travelling through the aimed joint region; stopping at
      // an empty pivot could miss the real moving skin a few centimetres behind.
      to: Object.fromEntries(
        ['x', 'y', 'z'].map((axis) => [
          axis,
          target[axis] + ((target[axis] - from[axis]) * 6) / length,
        ]),
      ),
      joint: index,
      age: 0,
      duration: (length + 6) / 36,
      done: false,
    });
  }
}
function stepStreams(state, dt, event) {
  const boss = state.boss,
    systems = boss.cinderheart;
  for (const stream of systems.streams) {
    stream.age += dt;
    if (stream.done) continue;
    const t = Math.min(1, stream.age / Math.max(0.01, stream.duration)),
      before = stream.head;
    stream.head = Object.fromEntries(
      ['x', 'y', 'z'].map((axis) => [
        axis,
        stream.from[axis] + (stream.to[axis] - stream.from[axis]) * t,
      ]),
    );
    const hit = skyAuthoredPoseContact(boss.authoredPose, before, stream.head);
    if (hit) {
      stream.head = { x: hit.x, y: hit.y, z: hit.z };
      stream.done = true;
      stream.endedAt = stream.age;
      const joint = boss.authoredPose.sockets[JOINTS[stream.joint]];
      //5.5m is the wetting radius across these measured, mountain-scale leg
      // roots. An intervening hull hit farther away blocks the jet entirely.
      if (
        ['crawl', 'charge', 'locked'].includes(systems.phase) &&
        Math.hypot(hit.x - joint.x, hit.y - joint.y, hit.z - joint.z) <= 5.5
      ) {
        freezeJoint(boss, stream.joint);
        stream.froze = true;
        event(state, 'cinder-joint-frozen', 'COOLANT CONTACT · JOINT HARDENED');
      }
    } else if (t >= 1) {
      stream.done = true;
      stream.endedAt = stream.age;
    }
  }
  systems.streams = systems.streams.filter(
    (stream) => !stream.done || stream.age - stream.endedAt < 0.65,
  );
}
function stepProps(state, dt, event) {
  for (const prop of skyCinderheartTargets(state)) {
    if (!prop.cinderheartTank) continue;
    prop.age += dt;
    if (prop.phase === 'ready') {
      prop.collisionEnabled = state.boss.cinderheart.phase !== 'approach';
      prop.weakpoint.exposed = prop.collisionEnabled;
      continue;
    }
    if (prop.phase === 'ruptured') {
      if (prop.age - dt < 2.4166667) propPose(prop, 'TankRupture', Math.min(2.4166667, prop.age));
      if (prop.age >= 12) {
        prop.phase = 'resetting';
        prop.age = 0;
        event(state, 'cinder-refill', 'COOLANT RESERVOIRS REPRESSURIZING');
      }
    } else if (prop.phase === 'resetting') {
      propPose(prop, 'ValveReset', prop.age);
      if (prop.age >= 2) {
        prop.phase = 'ready';
        prop.age = 0;
        prop.hp = prop.maxHp;
        prop.collisionEnabled = true;
        propPose(prop, 'Idle', 0);
      }
    }
  }
}
function enter(state, phase, event) {
  const boss = state.boss,
    systems = boss.cinderheart;
  systems.phase = phase;
  systems.age = 0;
  systems.volley = 0;
  boss.authoredTransition = boss.authoredPose;
  const text = {
    crawl: 'RUPTURE COOLANT · HARDEN ONE SIDE',
    charge: 'CHARGING TURN · HARDEN TWO JOINTS ON ONE SIDE',
    eruption: 'MOLTEN VOLLEY · DODGE',
    locked: 'BRITTLE JOINTS · SIDE COLLAPSE',
    rolling: 'LEG FRACTURE · MOVE TO THE EXPOSED FLANK',
    exposed: 'UNDERSIDE EXPOSED · STRIKE THE REACTOR',
    righting: 'RIGHTING · RESERVOIRS WILL REFILL',
  };
  event(state, 'cinder-phase', text[phase] ?? 'CALDERA APPROACH');
}

/** Pure, recoverable coolant→physical joint contact→charging turn→forced roll
 * sequence. Neither elapsed time nor shooting armor can manufacture a roll.
 * Repressurizing the two visible reservoirs permits another earned attempt.
 * Returns the exact authored clip and target role for116's shared pose owner. */
export function stepSkyCinderheart(state, dt, { event, fireAimed }) {
  const boss = state.boss,
    systems = boss.cinderheart;
  if (!systems) return null;
  stepProps(state, dt, event);
  // The real reservoirs stand on side hills above the boss's road-level vent.
  // Include their current source surfaces so fixed arenas cannot leave the
  // required manual controls above the pilot's reachable flight envelope.
  boss.authoredFlightCeiling = Math.max(
    boss.authoredFlightCeiling ?? 9,
    ...boss.authoredProps
      .filter((prop) => prop.cinderheartTank)
      .map((prop) => prop.weakpoint.y + 3),
  );
  stepStreams(state, dt, event);
  systems.age += dt;
  if (systems.age >= DURATION[systems.phase]) {
    const phase = systems.phase;
    if (phase === 'approach' || phase === 'eruption') enter(state, 'crawl', event);
    else if (phase === 'crawl') enter(state, 'charge', event);
    else if (phase === 'charge') {
      const side =
        systems.frozen[0] && systems.frozen[1]
          ? 1
          : systems.frozen[2] && systems.frozen[3]
            ? -1
            : 0;
      systems.lockedSide = side;
      enter(state, side ? 'locked' : 'eruption', event);
    } else if (phase === 'locked') {
      systems.frozen.forEach((frozen, i) => {
        if (frozen && !systems.broken[i]) {
          systems.broken[i] = true;
          systems.fracturedAt[i] = boss.age;
        }
      });
      systems.rolls++;
      enter(state, 'rolling', event);
    } else if (phase === 'rolling') enter(state, 'exposed', event);
    else if (phase === 'exposed') enter(state, 'righting', event);
    else if (phase === 'righting') {
      systems.frozen.fill(false);
      // Fractured original links retain their physical pose after coolant melts.
      enter(state, 'crawl', event);
    }
  }
  if (systems.phase === 'eruption') {
    const due = Math.min(3, Math.floor(systems.age / 0.35) + 1);
    while (systems.volley < due) {
      const first = state.threats.length;
      fireAimed(state, boss.attackSocket, 'fan', boss);
      markSkyCinderheartMolten(state, first);
      systems.volley++;
    }
  }
  systems.frozenPose = skyCinderPose(boss);
  const hardened = (indices) => indices.filter((index) => systems.frozen[index]).length;
  boss.controlHint =
    systems.phase === 'exposed'
      ? `STRIKE THE UNDERSIDE · ${Math.max(0, 6 - systems.age).toFixed(1)}s`
      : systems.phase === 'rolling'
        ? 'LEG FRACTURE · MOVE TO THE EXPOSED FLANK'
        : systems.phase === 'righting'
          ? 'DAMAGED STANCE · PREPARE THE NEXT COOLANT RELEASE'
          : systems.phase === 'locked'
            ? 'ONE SIDE HARDENED · PREPARE TO CLIMB'
            : systems.phase === 'eruption'
              ? 'MOLTEN VOLLEY · DODGE / BREAK COOLED ROCKS'
              : `RUPTURE COOLANT · LEFT ${hardened([2, 3])}/2 · RIGHT ${hardened([0, 1])}/2`;
  return {
    clip: CLIP[systems.phase],
    seconds: systems.age,
    target: systems.phase === 'exposed' ? 'underside_core' : 'vent_00',
    exposed: systems.phase === 'exposed' || (systems.phase === 'charge' && systems.age >= 1),
    overrides: systems.frozenPose,
  };
}

/** Called only after nearest source-triangle intersection. Reservoir rupture
 * requires manual weapon damage. Boss armor stays closed; raised vents and the
 * rolled underside accept only actual nearby surface hits, never socket planes.
 * This owner mutates HP once;81 records the corresponding impact and accuracy. */
export function resolveSkyCinderheartContact(
  state,
  target,
  shot,
  contact,
  { damage, autonomous },
  { event },
) {
  const boss = state.boss;
  if (!boss?.cinderheart) return undefined;
  if (target.cinderheartSlag) return resolveSkyCinderheartSlagContact(target, { damage });
  if (target.cinderheartTank) {
    if (target.phase !== 'ready' || !target.collisionEnabled || autonomous)
      return { handled: true, damage: 0, quality: 'armor' };
    const removed = Math.min(target.hp, Math.max(0, damage));
    target.hp -= removed;
    if (target.hp <= 0) {
      releaseCoolant(state, target);
      event(state, 'cinder-reservoir', 'COOLANT RESERVOIR RUPTURED');
    }
    return { handled: true, damage: removed, quality: 'body' };
  }
  if (target !== boss) return undefined;
  const systems = boss.cinderheart;
  const open = systems.phase === 'exposed' || (systems.phase === 'charge' && systems.age >= 1);
  if (!open) return { handled: true, damage: 0, quality: 'armor' };
  const targets =
    systems.phase === 'exposed'
      ? ['underside_core']
      : Array.from({ length: 8 }, (_, i) => `vent_0${i}`);
  const radius = systems.phase === 'exposed' ? 3 : 1.7;
  const onSurface = targets.some((name) => {
    const p = boss.authoredPose.sockets[name];
    return p && Math.hypot(p.x - contact.x, p.y - contact.y, p.z - contact.z) <= radius;
  });
  if (!onSurface) return { handled: true, damage: 0, quality: 'armor' };
  const removed = Math.min(boss.hp, Math.max(0, damage) * (systems.phase === 'exposed' ? 3 : 1));
  boss.hp -= removed;
  return {
    handled: true,
    damage: removed,
    quality: systems.phase === 'exposed' ? 'critical' : 'body',
  };
}
